import { useCallback, useEffect, useMemo, useState } from 'react'
import useQuestionField from '@v4/hooks/questions/useQuestionField'
import useQuestionSettingsField from '@v4/hooks/questions/useQuestionSettingsField'
import QuizQuestionsSelector from '@v4/utils/selectors/QuizQuestionsSelector'
import AppStore from '@v4/store/appStore'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import useStyles from './MultipleChoiceButtons.styled'

export const useMultipleChoiceButtons = props => {
	const { onClick, pending, justify, questionId, answers } = props
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const isQuestionBuilderMode = PageBuilderSelector.useIsQuestionsMode()
	const locked = ScorecardSelector.useIsQuestionsLocked()
	const options = useQuestionField(questionId, 'options')

	const multiple = useQuestionSettingsField('allow_multiple_select')
	const withOther = useQuestionSettingsField('other_answer')

	const [optionsExcludingOther, [otherOption]] = useMemo(() => {
		return _.partition(options, option => !option.open_text)
	}, [options])
	const {
		updateQuestionOption,
		deleteQuestionOption,
		addQuestionOption,
		sortQuestionOptions,
	} = QuizQuestionsSelector.useActions()

	const classes = useStyles({ ...props, multiple })

	const otherOptionId = useMemo(() => {
		const id = otherOption?.temporary_id || otherOption?.id

		if (!isBuilderMode) return id

		return withOther ? id : undefined
	}, [isBuilderMode, withOther, otherOption])

	const initialOtherAnswer = useMemo(() => {
		const answerOtherOption = _.find(answers, { option_id: otherOptionId })

		return answerOtherOption?.answer || ''
	}, [answers, otherOptionId])

	const [otherAnswer, setOtherAnswer] = useState(initialOtherAnswer)

	const [isDragging, setIsDragging] = useState(false)

	const [selectedOptions, setSelectedOptions] = useState(() => {
		if (!multiple) {
			return {}
		}

		return _.reduce(
			answers,
			(acc, { option_id: optionId }) => {
				if (optionId && _.some(options, { id: optionId })) {
					acc[optionId] = true
				}
				return acc
			},
			{},
		)
	})

	const handleClick = useCallback(
		({ optionId }) => {
			if (!optionId) {
				return
			}
			setSelectedOptions(values =>
				multiple
					? {
							...values,
							[optionId]: !values[optionId],
					  }
					: {
							[optionId]: true,
					  },
			)

			if (!multiple) {
				onClick(
					optionId === otherOptionId ? { answer: otherAnswer, options: [optionId] } : { options: [optionId] },
				)
			}
		},
		[multiple, onClick, otherAnswer, otherOptionId],
	)

	const handleChange = useCallback(
		option => {
			updateQuestionOption({ questionId, option })
		},
		[questionId, updateQuestionOption],
	)

	const handleDelete = useCallback(
		id => {
			deleteQuestionOption({ questionId, id })
		},
		[deleteQuestionOption, questionId],
	)

	const handleAdd = useCallback(() => {
		addQuestionOption({
			id: questionId,
			option: {
				option: '',
			},
		})
	}, [addQuestionOption, questionId])

	const handleSubmit = useCallback(() => {
		const optionIds = Object.keys(selectedOptions).filter(key => selectedOptions[key])

		const withOtherId = _.includes(optionIds, otherOptionId?.toString())

		onClick(withOtherId ? { options: optionIds, answer: otherAnswer } : { options: optionIds })
	}, [onClick, otherAnswer, otherOptionId, selectedOptions])

	const handleSortStart = useCallback(() => {
		setIsDragging(true)
	}, [])

	const handleSortEnd = useCallback(
		({ oldIndex, newIndex }) => {
			setIsDragging(false)
			sortQuestionOptions({ questionId, oldIndex, newIndex })
		},
		[questionId, sortQuestionOptions],
	)

	const handleOtherChange = useCallback(
		answer => {
			setOtherAnswer(answer)

			const checked = selectedOptions[otherOptionId]

			if (multiple && ((checked && !answer) || (!checked && answer))) {
				handleClick({ optionId: otherOptionId, answer })
			}
		},
		[handleClick, multiple, otherOptionId, selectedOptions],
	)

	useEffect(() => {
		if (!multiple) {
			return undefined
		}

		const handleKeyUp = ({ key }) => {
			if (key === 'Enter') {
				handleSubmit()
			}
		}

		document.addEventListener('keyup', handleKeyUp)
		return () => {
			document.removeEventListener('keyup', handleKeyUp)
		}
	}, [handleSubmit, multiple])

	return [
		{
			classes,
			justify,
			options: optionsExcludingOther,
			pending,
			selectedOptions,
			gridWidth: _.size(options) === 3 ? 4 : 6,
			withEdition: !locked && isBuilderMode && isQuestionBuilderMode,
			type: multiple ? 'checkbox' : 'button',
			multiple,
			isDragging,
			otherOptionId,
			otherAnswer,
		},
		{
			handleOtherChange,
			handleClick,
			handleChange,
			handleDelete,
			handleAdd,
			handleSubmit,
			handleSortStart,
			handleSortEnd,
		},
	]
}
